<template>
  <div class="py-5 md:px-5">
    <PageHeader title="Routes" class="container">
      <button  class="btn btn-secondary" @click="showSyncRoutesModal = true">
        <fa :icon="['far', 'exchange']" class="mr-2" /> Sync Routes
      </button>
    </PageHeader>

    <Modal title="Sync Routes" :open.sync="showSyncRoutesModal">
      <div class="p-6">
        <label for="" class="form-label">Select a Depot</label>
        <DepotSelect v-model="selectedDepot" @input="fetchDepot" />
        <Collapse v-if="syncDepot" :active="syncDepot !== null" :duration="200">
          <div class="grid grid-cols-3 p-4 mt-4 border rounded bg-gray-50">
            <label class="form-label">Project:</label> 
            <div class="col-span-2">{{ syncDepot.ptv_project }}</div>
            <label class="form-label">Profile:</label> 
            <div class="col-span-2">{{ syncDepot.ptv_profile }}</div>
          </div>
          <button v-if="syncDepot.ptv_integration" class="w-full mt-4 btn btn-primary" v-submitting="syncing" @click="syncRoutes">
            Sync Routes
          </button>
          <button v-else class="w-full mt-4 btn">
            No PTV Integration For Depot
          </button>
        </Collapse>
      </div>
    </Modal>

    <div class="container">
      <!-- List -->
      <div class="card">
        <div class="flex flex-col justify-between p-4 border-b md:flex-row lg:p-5">
          <div class="flex flex-col space-y-3 md:space-y-0 md:flex-row md:space-x-3">
            <FilterDropdown @apply="applyFilter" @clear="clearFilter" :loading="loading">
              <div>
                <label for="date_range" class="form-label">Date Range</label>
                <DatePicker color="indigo" mode="dateTime" is-range is24hr :model-config="{ start: { timeAdjust: '00:00:00' }, end: { timeAdjust: '23:59:59' } }" v-model="filter.date_range">
                  <template v-slot="{ inputValue, inputEvents }">
                    <div class="flex justify-center">
                      <input
                        :value="inputValue.start"
                        v-on="inputEvents.start"
                        class="form-input prepend"
                      />
                      <div class="flex items-center justify-center flex-none w-10 text-gray-500 border-t border-b border-gray-300">
                        &rarr;
                      </div>
                      <input
                        :value="inputValue.end"
                        v-on="inputEvents.end"
                        class="form-input append"
                      />
                    </div>
                  </template>
                </DatePicker>
              </div>
              <div>
                <label for="type" class="form-label">Depot</label>
                <DepotSelect v-model="filter.depot" />
              </div>
              <div>
                <label for="type" class="form-label">Driver</label>
                <DriverSelect v-model="filter.driver" />
              </div>
              <div>
                <label for="type" class="form-label">Vehicle</label>
                <VehicleSelect v-model="filter.vehicle" />
              </div>
            </FilterDropdown>
            <SortDropdown :options="sortOptions" />
          </div>
        </div>

        <!-- Loading -->
        <Loading v-if="loading" class="p-6" />

        <!-- No Results -->
        <div v-else-if="routes.meta.total < 1" class="max-w-sm py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            No routes logged yet.
          </div>
          <div class="text-gray-500">
            Routes will automatically be logged here when they are detected in your integrations.
          </div>
        </div>

        <!-- Listing -->
        <div class="divide-y" v-else>
          <div class="grid grid-cols-5 px-4 lg:px-5 py-3 text-gray-500 uppercase bg-gray-50 text-xs font-medium ] tracking-wider">
            <div>Route</div>
            <div>Started</div>
            <div>Ended</div>
            <div>Driver</div>
            <div>Vehicle</div>
          </div>
          <div v-for="route in routes.data" :key="route.id">
            <router-link :to="{ name: 'RouteView', params: { id: route.id } }" class="grid grid-cols-5 p-4 text-gray-900 hover:no-underline lg:px-5 hover:bg-gray-50">
              <div>
                <div class="font-bold">
                  {{ route.name }}
                </div>
                <div class="text-gray-500">
                  {{ route.stops_count }} {{ route.stops_count | pluralize('stop') }}
                </div>
              </div>
              <div>
                <div v-if="route.start_depot">{{ route.start_depot.name }}</div>
                <div class="text-gray-500">
                  {{ route.planned_start_time | moment('HH:mm DD/MM/YYYY') }}
                </div>
              </div>
              <div>
                <div v-if="route.end_depot">{{ route.end_depot.name }}</div>
                <div class="text-gray-500">
                  {{ route.planned_end_time | moment('HH:mm DD/MM/YYYY') }}
                </div>
              </div>
              <div v-if="route.driver">
                <div>{{ route.driver.name }}</div>
                <div class="text-gray-500">{{ route.driver.driverno }}</div>
              </div>
              <div v-if="route.vehicle">
                <div>{{ route.vehicle.name }}</div>
                <div class="text-gray-500">{{ route.vehicle.objectno }}</div>
              </div>
            </router-link>
          </div>
          <!-- Pagination -->
          <Pagination class="rounded-b-lg" :total="routes.meta.total" :per-page="routes.meta.per_page" :current-page="routes.meta.current_page" />
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { sub, startOfDay, endOfDay } from 'date-fns'
import FilterDropdown from '@/components/FilterDropdown'
import SortDropdown from '@/components/SortDropdown'
import DepotSelect from '@/components/DepotSelect'
import DriverSelect from '@/components/DriverSelect'
import VehicleSelect from '@/components/VehicleSelect'

export default {
  name: 'RouteList',

  metaInfo: {
    title: 'Routes'
  },

  components: {
    FilterDropdown,
    SortDropdown,
    DepotSelect,
    DriverSelect,
    VehicleSelect
  },

  created () {
    this.setFilter()
    this.getRoutes()
  },

  data () {
    return {
      loading: false,
      syncing: false,
      selectedDepot: null,
      syncDepot: null,
      showSyncRoutesModal: false,
      routes: {},
      filter: {
        date_range: { 
          start: startOfDay(sub(new Date(), { months: 1 })), 
          end: endOfDay(new Date())
        },
        depot: null,
        driver: null,
        vehicle: null
      },
      sortOptions: [
        { label: `Start Time <span class="text-gray-500">(Newest First)</span>`, prop: 'planned_start_time', dir: 'desc' },
        { label: `Start Time <span class="text-gray-500">(Oldest First)</span>`, prop: 'planned_start_time', dir: 'asc' }
      ]
    }
  },

  methods: {
    async getRoutes () {
      try {
        this.loading = true
        const { data } = await this.$api.get(`account/${this.$route.params.account}/routes`, {
          params: {
            page: this.$route.query.page || 1,
            sort: this.$route.query.sort || 'planned_start_time',
            dir: this.$route.query.dir || 'desc',
            ...this.filter && { filter: this.filter }
          }
        })
        this.routes = data.routes
        this.loading = false
      } catch (error) {
        console.error(error)
      }
    },

    setFilter () {
      if (this.$route.query.filter) {
        this.filter = JSON.parse(this.$route.query.filter)
      }
    },

    applyFilter () {
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: JSON.stringify(this.filter)
        } 
      })
    },

    clearFilter () {
      this.filter = {
        type: null
      }
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: undefined
        } 
      })
    },

    async fetchDepot () {
      try {
        const { data } = await this.$api.get(`account/${this.$route.params.account}/depots/${this.selectedDepot}`)
        this.syncDepot = data.depot
      } catch (error) {
        console.error(error)
      }
    },

    async syncRoutes () {
      try {
        this.syncing = true
        await this.$api.get(`account/${this.$route.params.account}/sync/routes/${this.syncDepot.id}`)
        this.syncing = false
      } catch (error) {
        console.error(error)
      }
    }
  },

   watch: {
    '$route.query': {
      handler (newVal, oldVal) {
        this.getRoutes()
      },
      deep: true
    }
  }
}
</script>